import {BaseApi} from './BaseApi'

// ============================================
// ENUMS
// ============================================

export enum RoleCategory {
  Platform = 'Platform',
  MSP = 'MSP',
  Agency = 'Agency',
  Company = 'Company',
}

export enum RoleStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

// ============================================
// MODULE PERMISSION INTERFACES
// ============================================

export interface SubmodulePermission {
  key: string
  name: string
  canView?: boolean
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

export interface ModulePermission {
  key: string
  name: string
  canView?: boolean
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
  submodules?: SubmodulePermission[]
}

// ============================================
// ROLE ACCESS MODEL (Legacy)
// ============================================

export interface RoleAccessModel {
  entityType: string
  scope: string
  operations: string[]
}

// ============================================
// ROLE INTERFACES
// ============================================

export interface Role {
  id: string
  _id?: string // Legacy, prefer using `id`
  name: string
  accessList: RoleAccessModel[]
  isPlatform?: boolean
  category?: RoleCategory
  status?: RoleStatus
  owner?: string
  ownerId?: string
  isEditable?: boolean
  isSystemRole?: boolean
  modulePermissions?: ModulePermission[]
  description?: string
  createdDate?: string
  modifiedDate?: string
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
}

export interface RoleAccessLevelResponse {
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
}

export interface RoleListItemResponse {
  id: string
  name: string
  displayName?: string
  category: RoleCategory
  accessLevel: RoleAccessLevelResponse
  status: RoleStatus
  modifiedDate: Date
  userCount: number
  owner: string
  ownerId?: string
  isEditable: boolean
  isSystemRole: boolean
}

export interface RoleListPagingResponse {
  roles: RoleListItemResponse[]
  totalRoles: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

export interface RoleDetailResponse {
  id: string
  name: string
  displayName?: string
  category: RoleCategory
  status: RoleStatus
  owner: string
  ownerId?: string
  isEditable: boolean
  isSystemRole: boolean
  description?: string
  accessList: RoleAccessModel[]
  modulePermissions: ModulePermission[]
  createdDate: Date
  modifiedDate: Date
  userCount: number
  versionKey: string
}

// ============================================
// REQUEST INTERFACES
// ============================================

export interface GetRolesQuery {
  pageNumber?: number
  pageSize?: number
  category?: RoleCategory
  status?: RoleStatus
  search?: string
  ownerId?: string
  includeSystemRoles?: boolean
}

/**
 * Query params for getting creatable roles (filtered by current user's hierarchy)
 */
export interface GetCreatableRolesQuery {
  category?: RoleCategory
  ownerId?: string
  includeSystemRoles?: boolean
}

export interface CreateRoleRequest {
  name: string
  category: RoleCategory
  accessList?: RoleAccessModel[]
  isPlatform?: boolean
  status?: RoleStatus
  modulePermissions?: ModulePermission[]
  description?: string
  ownerId?: string
  owner?: string
}

export interface CloneRoleRequest {
  newName: string
  companyId: string
  companyName?: string
  description?: string
}

export interface RoleUpdateModel {
  id: string
  name?: string
  status?: RoleStatus
  description?: string
  modulePermissions?: ModulePermission[]
  accessList?: RoleAccessModel[]
  isPlatform?: boolean
  documentVersion?: string // For optimistic concurrency
}

// ============================================
// USER BY ROLE INTERFACES
// ============================================

export interface RoleUserResponse {
  id: string
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber?: string
  companyId?: string
  companyName?: string
  agencyId?: string
  agencyName?: string
  status: string
  createdDate?: string
  createdBy?: string
}

export interface RoleUsersPagingResponse {
  roleName: string
  users: RoleUserResponse[]
  totalUsers: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

export interface UserPagingModel {
  users: RoleUserResponse[]
  totalUsers: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

// ============================================
// PAGINATION PARAMS
// ============================================

export interface PaginationParams {
  pageNumber?: number
  pageSize?: number
}

// ============================================
// PERMISSIONS RESPONSE (Login Flow)
// ============================================

/**
 * Response from GET /roles/permissions?roles=role1,role2
 * Used during login to get merged permissions for user's roles
 */
export interface RolePermissionsResponse {
  /** Array of full role details for each role name requested */
  roles: RoleDetailResponse[]
  /** Merged modulePermissions from all roles - use this for UI visibility */
  mergedPermissions: ModulePermission[]
}

// ============================================
// ROLE API SERVICE
// ============================================

export class RoleApiService extends BaseApi {
  protected rolesPath = '/roles'
  protected userPath = '/user'

  // ============================================
  // ROLE ENDPOINTS
  // ============================================

  /**
   * Get roles with filters and pagination
   * GET /roles
   */
  async getRoles(query?: GetRolesQuery): Promise<RoleListPagingResponse> {
    try {
      const response = await this.client.get<RoleListPagingResponse>(this.rolesPath, {
        params: query,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching roles:', error)
      throw error
    }
  }

  /**
   * Get all roles (legacy - no filtering)
   * GET /roles/all
   */
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await this.client.get<Role[]>(`${this.rolesPath}/all`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching all roles:', error)
      throw error
    }
  }

  /**
   * Get roles that the current user can assign to new users.
   * Filtered by role hierarchy - users can only assign roles with equal or lower rank.
   * GET /roles/creatable
   */
  async getCreatableRoles(query?: GetCreatableRolesQuery): Promise<RoleListItemResponse[]> {
    try {
      const response = await this.client.get<RoleListItemResponse[]>(`${this.rolesPath}/creatable`, {
        params: query,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching creatable roles:', error)
      throw error
    }
  }

  /**
   * Get role by ID with full details
   * GET /roles/byId/:id
   */
  async getRoleById(roleId: string): Promise<RoleDetailResponse> {
    try {
      const response = await this.client.get<RoleDetailResponse>(`${this.rolesPath}/byId/${roleId}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching role by ID:', roleId, error)
      throw error
    }
  }

  /**
   * Get role by name (legacy)
   * GET /roles/byName/:name
   */
  async getRoleByName(roleName: string): Promise<Role> {
    try {
      const response = await this.client.get<Role>(`${this.rolesPath}/byName/${roleName}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching role by name:', roleName, error)
      throw error
    }
  }

  /**
   * Get merged permissions for multiple roles (LOGIN FLOW)
   * GET /roles/permissions?roles=platformadmin,agencyadmin
   *
   * This is the primary endpoint for the sign-in flow:
   * 1. User signs in
   * 2. Get user's roles from /user/userByEmail response
   * 3. Call this endpoint with role names to get merged permissions
   * 4. Store mergedPermissions in Redux for UI visibility
   *
   * @param roleNames - Array of role names (e.g., ['platformadmin', 'agencyadmin'])
   * @returns Full role details and merged permissions across all roles
   */
  async getPermissionsForRoles(roleNames: string[]): Promise<RolePermissionsResponse> {
    try {
      const rolesParam = roleNames.join(',')
      const response = await this.client.get<RolePermissionsResponse>(
        `${this.rolesPath}/permissions`,
        {params: {roles: rolesParam}},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching permissions for roles:', roleNames, error)
      throw error
    }
  }

  /**
   * Get users assigned to a role
   * GET /roles/:id/users
   */
  async getUsersByRoleId(
    roleId: string,
    pagination?: PaginationParams,
  ): Promise<RoleUsersPagingResponse> {
    try {
      const response = await this.client.get<RoleUsersPagingResponse>(
        `${this.rolesPath}/${roleId}/users`,
        {params: pagination},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching users for role:', roleId, error)
      throw error
    }
  }

  /**
   * Get user count for a role
   * GET /roles/:id/users/count
   */
  async getUserCountByRoleId(roleId: string): Promise<number> {
    try {
      const response = await this.client.get<number>(`${this.rolesPath}/${roleId}/users/count`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching user count for role:', roleId, error)
      throw error
    }
  }

  /**
   * Create a new role
   * POST /roles
   */
  async createRole(request: CreateRoleRequest): Promise<Role> {
    try {
      const response = await this.client.post<Role>(this.rolesPath, request)
      return response.data
    } catch (error: unknown) {
      console.error('Error creating role:', error)
      throw error
    }
  }

  /**
   * Create a new role (legacy endpoint)
   * POST /roles/createRole
   * @deprecated Use createRole() instead
   */
  async createRoleLegacy(request: CreateRoleRequest): Promise<Role> {
    try {
      const response = await this.client.post<Role>(`${this.rolesPath}/createRole`, request)
      return response.data
    } catch (error: unknown) {
      console.error('Error creating role (legacy):', error)
      throw error
    }
  }

  /**
   * Clone a system role for a company
   * POST /roles/:id/clone
   * Note: Only Company category system roles can be cloned
   */
  async cloneRole(roleId: string, request: CloneRoleRequest): Promise<Role> {
    try {
      const response = await this.client.post<Role>(`${this.rolesPath}/${roleId}/clone`, request)
      return response.data
    } catch (error: unknown) {
      console.error('Error cloning role:', roleId, error)
      throw error
    }
  }

  /**
   * Update a role
   * PUT /roles/:id
   * Note: System roles can only be modified by platform admins
   * Note: isEditable=false roles cannot be updated
   */
  async updateRole(roleId: string, request: RoleUpdateModel): Promise<Role> {
    try {
      const response = await this.client.put<Role>(`${this.rolesPath}/${roleId}`, {
        ...request,
        id: roleId,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error updating role:', roleId, error)
      throw error
    }
  }

  /**
   * Delete a role
   * DELETE /roles/:id
   * Note: Cannot delete if users are assigned
   * Note: Cannot delete system roles or isEditable=false roles
   */
  async deleteRole(roleId: string): Promise<Role> {
    try {
      const response = await this.client.delete<Role>(`${this.rolesPath}/${roleId}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error deleting role:', roleId, error)
      throw error
    }
  }

  // ============================================
  // USER BY ROLE ENDPOINTS
  // ============================================

  /**
   * Get users by role name with pagination
   * GET /user/byRole/:roleName
   */
  async getUsersByRoleName(
    roleName: string,
    pagination?: PaginationParams,
  ): Promise<UserPagingModel> {
    try {
      const response = await this.client.get<UserPagingModel>(
        `${this.userPath}/byRole/${roleName}`,
        {params: pagination},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching users by role name:', roleName, error)
      throw error
    }
  }

  /**
   * Count users by role name
   * GET /user/byRole/:roleName/count
   */
  async countUsersByRoleName(roleName: string): Promise<number> {
    try {
      const response = await this.client.get<number>(`${this.userPath}/byRole/${roleName}/count`)
      return response.data
    } catch (error: unknown) {
      console.error('Error counting users by role name:', roleName, error)
      throw error
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Get roles for a specific category with optional filtering
   */
  async getRolesByCategory(
    category: RoleCategory,
    options?: {
      status?: RoleStatus
      search?: string
      pageNumber?: number
      pageSize?: number
    },
  ): Promise<RoleListPagingResponse> {
    return this.getRoles({
      category,
      ...options,
    })
  }

  /**
   * Get all active roles for a category
   */
  async getActiveRolesByCategory(category: RoleCategory): Promise<RoleListPagingResponse> {
    return this.getRoles({
      category,
      status: RoleStatus.Active,
      pageSize: 100, // Get all active roles
    })
  }

  /**
   * Get company-specific roles (system + company-owned)
   */
  async getCompanyRoles(
    companyId: string,
    options?: {
      status?: RoleStatus
      search?: string
      pageNumber?: number
      pageSize?: number
    },
  ): Promise<RoleListPagingResponse> {
    return this.getRoles({
      category: RoleCategory.Company,
      ownerId: companyId,
      includeSystemRoles: true,
      ...options,
    })
  }

  /**
   * Check if a role can be deleted
   * Returns error message if cannot delete, null if OK
   */
  async canDeleteRole(roleId: string): Promise<string | null> {
    try {
      const role = await this.getRoleById(roleId)

      if (role.isSystemRole) {
        return 'System roles cannot be deleted'
      }

      if (!role.isEditable) {
        return 'This role cannot be deleted'
      }

      if (role.userCount > 0) {
        return `Cannot delete role with ${role.userCount} assigned user(s)`
      }

      return null
    } catch (error: unknown) {
      console.error('Error checking if role can be deleted:', roleId, error)
      throw error
    }
  }

  /**
   * Check if a role can be edited
   */
  async canEditRole(roleId: string): Promise<{canEdit: boolean; reason?: string}> {
    try {
      const role = await this.getRoleById(roleId)

      if (!role.isEditable) {
        return {canEdit: false, reason: 'This role is not editable'}
      }

      // Note: System roles can only be edited by platform admins
      // This check should be done in the UI based on current user's role
      if (role.isSystemRole) {
        return {canEdit: true, reason: 'Only platform admins can edit system roles'}
      }

      return {canEdit: true}
    } catch (error: unknown) {
      console.error('Error checking if role can be edited:', roleId, error)
      throw error
    }
  }
}

// Export singleton instance
export const roleApi = new RoleApiService()

// Re-export moduleApi types and service for convenience
export {
  moduleApi,
  type ModuleDefinition,
  type SubmoduleDefinition,
  type CategoryModuleConfig,
} from './moduleApi'
